/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, type JSX } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch } from '@/scapi';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/icon';
import { useTranslation } from 'react-i18next';
import { useClearAllFilters } from '../use-clear-all-filters';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';
import { type TFunction } from 'i18next';
// @sfdc-extension-block-end SFDC_EXT_BOPIS

// Get human-readable label for a specific value
const getValueLabel = (
    attributeId: string,
    value: string,
    refinements: ShopperSearch.schemas['ProductSearchRefinement'][],
    // @sfdc-extension-line SFDC_EXT_BOPIS
    selectedStoreInfo: { inventoryId?: string; name?: string } | null,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    tBopis: TFunction<'extBopis'>
): string => {
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    if (attributeId === 'ilids') {
        // If the filter value matches the selected store's inventory ID and we have a store name
        if (selectedStoreInfo?.name) {
            return tBopis('storeInventoryFilter.label', { storeName: selectedStoreInfo.name });
        }
        return tBopis('storeInventoryFilter.inStock');
    }
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    const refinement = refinements.find((r) => r.attributeId === attributeId);
    const valueObj = refinement?.values?.find((v) => v.value === value);
    return valueObj?.label || value;
};

export default function CategoryFilters({
    result,
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
}): JSX.Element | null {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';
    const refinements = useMemo(() => result?.refinements || [], [result]);

    /**
     * Optimistic search string derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, allowing us to read the
     * intended refinements immediately. Once the navigation settles, we fall back to the current location.
     */
    const effectiveSearch = navigation.location ? navigation.location.search : location.search;

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const selectedStoreInfo = useStoreLocator((s) => s.selectedStoreInfo);
    const { t: tBopis } = useTranslation('extBopis');
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    const activeFilters = useMemo(() => {
        const params = new URLSearchParams(effectiveSearch);
        const refines = params.getAll('refine');
        const filters: Array<{
            attributeId: string;
            value: string;
            valueLabel: string;
        }> = [];

        for (const refine of refines) {
            const separatorIndex = refine.indexOf('=');
            if (separatorIndex === -1) continue;

            const attributeId = refine.substring(0, separatorIndex);
            const value = refine.substring(separatorIndex + 1);
            // `cgid` is kept in the URL for behaviour and clear-all, but gets no chip: category
            // selection is navigation here, not filtering, so the rail offers no Category group to
            // remove it from. See the note in `category-refinements/index.tsx`.
            if (attributeId === 'cgid') continue;

            const valueLabel = getValueLabel(
                attributeId,
                value,
                refinements,
                // @sfdc-extension-line SFDC_EXT_BOPIS
                selectedStoreInfo,
                // @sfdc-extension-line SFDC_EXT_BOPIS
                tBopis
            );
            filters.push({
                attributeId,
                value,
                valueLabel,
            });
        }
        return filters;
    }, [
        effectiveSearch,
        refinements,
        // @sfdc-extension-block-start SFDC_EXT_BOPIS
        selectedStoreInfo,
        tBopis,
        // @sfdc-extension-block-end SFDC_EXT_BOPIS
    ]);

    // Remove a specific filter
    const removeFilter = useCallback(
        (attributeId: string, value: string) => {
            const params = new URLSearchParams(location.search);
            const refines = params.getAll('refine');
            const refinePair = `${attributeId}=${value}`;

            // Remove this specific refinement
            params.delete('refine');
            refines.filter((r) => r !== refinePair).forEach((r) => params.append('refine', r));

            params.set('offset', '0');
            // `preventScrollReset` for the same reason as the rail's `toggleFilter`
            // (`category-refinements/index.tsx`): this is a real navigation, and <ScrollRestoration>
            // would otherwise send the shopper to the top. Removing a filter here and unticking the
            // same filter in the rail are one interaction to a shopper, so they must not differ.
            return navigate(
                {
                    ...location,
                    search: `?${params.toString()}`,
                },
                { preventScrollReset: true }
            );
        },
        [location, navigate]
    );

    /** Shared with the overlay's `Clear Filters` and the over-filtered message — see the hook's note. */
    const clearAllFilters = useClearAllFilters();

    // Don't render if no active filters
    if (activeFilters.length === 0) {
        return null;
    }

    return (
        <div className={isPending ? 'pointer-events-none opacity-50 transition-opacity' : undefined}>
            {/*
             * No heading. The design (Figma `1396:10895`) shows the chips and `Clear all` on their
             * own, with nothing introducing them — the applied filters are self-describing, and the
             * `APPLIED FILTERS:` label that used to sit here is not in the design. The
             * `categoryRefinements:appliedFilters` key is left in the locale files because the
             * chip row still needs an accessible name, which it now carries on the region itself.
             *
             * Measured from the chips row `0:706` on that frame:
             *
             * | Part | Value |
             * |---|---|
             * | row height | **32px** — every child, chips and `Clear all` alike |
             * | chip gap | 8px (`0`→`83`, next at `91`; `91+115=206`, next at `214`) |
             * | label | `body/default` 16px, weight 450, 3% tracking |
             * | label colour | `--text-color-secondary` #585858 -> `--muted-foreground` |
             *
             * So the chips are `size="sm"` (`h-8 px-3 text-base`), NOT the default 48px/18px step.
             * The row also carries **no bottom rule** — the frame has no stroke, and the only rule
             * in this area of the design is the one under the title block, which CRCB2B-87 owns.
             */}
            <div
                role="region"
                aria-label={t('categoryRefinements:appliedFilters')}
                className="mb-4 flex flex-wrap items-center gap-2">
                {activeFilters.map(({ attributeId, value, valueLabel }) => (
                    <Button
                        key={`${attributeId}:${value}`}
                        variant="outline"
                        size="sm"
                        // The 20px close glyph comes from the `sm` size variant, so `Icon`
                        // deliberately takes no `size` — an inline font-size would outrank the
                        // class and desynchronise the glyph from the button's size step.
                        //
                        // WEIGHT IS 500, NOT the design's 450. `font-book` cannot be applied here:
                        // `cn()`'s tailwind-merge is extended only for `border-ui`, so `font-book`
                        // is not in the `font-weight` class group and does NOT displace the
                        // button's own `font-medium` — both survive and stylesheet order decides.
                        // Registering it globally is the right fix but moves 75 snapshots across
                        // 24 unrelated components, so it belongs to its own change, not this one.
                        className="cursor-pointer bg-muted text-muted-foreground hover:bg-muted-hover"
                        onClick={() => void removeFilter(attributeId, value)}>
                        {valueLabel}
                        <Icon name="close" />
                    </Button>
                ))}
                <Button
                    variant="link"
                    size="sm"
                    className="cursor-pointer text-muted-foreground underline hover:text-foreground"
                    onClick={clearAllFilters}>
                    {t('categoryRefinements:clearAll')}
                </Button>
            </div>
        </div>
    );
}
